import { useCallback, useRef, useState } from 'react';
import { hasWindow } from '@/lib/notifications/dom';

const STORAGE_KEY = 'notifi:inbox-sound-enabled';

function readStoredPreference(): boolean {
  if (!hasWindow()) return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeStoredPreference(enabled: boolean): void {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Private mode / disabled storage — silent fail is fine.
  }
}

export interface UseNotificationSoundResult {
  enabled: boolean;
  setEnabled: (next: boolean) => void;
  /** Plays the ping if enabled. No-op when disabled. */
  play: () => void;
}

/**
 * Opt-in "new message" ping. The preference is persisted in localStorage so
 * the user only has to enable it once.
 *
 * Synthesised via Web Audio API (no binary asset to bundle / version). A
 * short two-tone chime: 880Hz then 660Hz, ~180ms total — quiet enough to
 * not be jarring, distinct enough to read as "ping" rather than a
 * background blip.
 */

// Modern AudioContext lives at `window.AudioContext`; older WebKit exposes it
// as `webkitAudioContext`. Detect both without forcing the legacy global into
// the page's type space.
type AudioContextCtor = typeof AudioContext;
function resolveAudioContextCtor(): AudioContextCtor | null {
  if (!hasWindow()) return null;
  const w = window as Window & {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Plays a synthesised two-tone "ping" through the supplied AudioContext.
 * Each tone uses a sine wave envelope (very short attack, gentle decay) so
 * the result reads as a chime rather than a click.
 */
function playPing(ctx: AudioContext): void {
  const now = ctx.currentTime;
  const tones: Array<{ freq: number; start: number; duration: number }> = [
    { freq: 880, start: 0, duration: 0.09 },
    { freq: 660, start: 0.09, duration: 0.09 },
  ];
  for (const { freq, start, duration } of tones) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const gain = ctx.createGain();
    // Envelope: ramp up over 5ms then exponential decay so the trailing
    // edge doesn't click. Cap peak at 0.25 — loud enough to hear over a
    // typical office laptop speaker but well shy of "startling".
    const t0 = now + start;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.25, t0 + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + duration + 0.02);
  }
}

export function useNotificationSound(): UseNotificationSoundResult {
  const [enabled, setEnabledState] = useState<boolean>(() => readStoredPreference());
  // Reuse a single AudioContext across plays. Created lazily on the first
  // play() so we don't allocate it (or trip browser-autoplay heuristics)
  // for users who keep sound disabled.
  const ctxRef = useRef<AudioContext | null>(null);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    writeStoredPreference(next);
  }, []);

  const play = useCallback(() => {
    if (!enabled) return;
    const Ctor = resolveAudioContextCtor();
    if (!Ctor) return;
    try {
      if (!ctxRef.current) ctxRef.current = new Ctor();
      const ctx = ctxRef.current;
      // Some browsers start the context in `suspended` state until the
      // page has had a user gesture. resume() is a no-op when already
      // running; if it rejects (gesture not yet observed) the play is
      // simply skipped this round.
      if (ctx.state === 'suspended') {
        void ctx.resume().then(
          () => playPing(ctx),
          () => undefined,
        );
        return;
      }
      playPing(ctx);
    } catch {
      // AudioContext can throw in detached frames / private modes.
      // Swallow — silence is preferable to a broken render.
    }
  }, [enabled]);

  return { enabled, setEnabled, play };
}
